// sections/@dashboard/finance/ReconciliationPanel.tsx
// ─── COMPLETE REWRITE ────────────────────────────────────────────────────────
// What's new vs old version:
//  1. "Create Batch" button — lets user define a period and create a batch
//  2. Assign category inline — unmatched rows open AssignCategoryDialog
//  3. Mark as reviewed — sets reconciliationStatus = reconciled without a batch
//  4. Category name shown in table (not just the raw ID)
//  5. Proper loading states for every action
//  6. All batches are fetched with their bank account details

import { useEffect, useState } from 'react';
import {
  Stack,
  Grid,
  Card,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import { fCurrency } from '../../../utils/formatNumber';
import { fDate } from '../../../utils/formatTime';
import {
  IBankTransaction,
  IReconciliationBatch,
  ITransactionCategory,
} from '../../../@types/finance';
import { FinanceService } from '../../../api/finance.service';
import AssignCategoryDialog from '../../../components/finance/AssignCategoryDialog';
import { pluralize } from '../../../utils/pluralize';

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({
  label,
  value,
  color,
  icon,
  sub,
}: {
  label: string;
  value: string | number;
  color: 'success' | 'error' | 'warning' | 'info';
  icon: string;
  sub?: string;
}) {
  return (
    <Card sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${color}.lighter`,
          color: `${color}.main`,
          flexShrink: 0,
        }}
      >
        <Iconify icon={icon} width={22} />
      </Box>
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontFamily="monospace" color={`${color}.dark`}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.disabled">
            {sub}
          </Typography>
        )}
      </Stack>
    </Card>
  );
}

// ── Create Batch Dialog ───────────────────────────────────────────────────────
function CreateBatchDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [periodStart, setPeriodStart] = useState<Date | null>(null);
  const [periodEnd, setPeriodEnd] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!periodStart || !periodEnd) return;
    try {
      setSaving(true);
      const svc = new FinanceService();
      // POST /finance/reconciliation/batches
      await svc.createReconciliationBatch({
        // Local calendar day, not toISOString(), so the picked date never shifts.
        periodStart: fDate(periodStart, 'yyyy-MM-dd'),
        periodEnd: fDate(periodEnd, 'yyyy-MM-dd'),
      });
      enqueueSnackbar('Reconciliation batch created!');
      onCreated();
      onClose();
    } catch {
      enqueueSnackbar('Failed to create batch', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create Reconciliation Batch</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the date range to reconcile. All transactions in this period
          that have a category assigned will be marked as reconciled.
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={2}>
            <DatePicker
              label="Period start"
              value={periodStart}
              onChange={setPeriodStart}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <DatePicker
              label="Period end"
              value={periodEnd}
              minDate={periodStart ?? undefined}
              onChange={setPeriodEnd}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!periodStart || !periodEnd || saving}
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : undefined
          }
        >
          {saving ? 'Creating…' : 'Create & Reconcile'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
interface Props {
  transactions: IBankTransaction[];
  categories: ITransactionCategory[];
  onRefresh: () => void;
}

export default function ReconciliationPanel({
  transactions,
  categories,
  onRefresh,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [batches, setBatches] = useState<IReconciliationBatch[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [reconcilingId, setReconcilingId] = useState<number | null>(null);
  const [createBatchOpen, setCreateBatchOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState<IBankTransaction | null>(null);
  const [markingReviewed, setMarkingReviewed] = useState<number | null>(null);

  // ── Derived stats from the transactions prop ───────────────────────────────
  const matched = transactions.filter(
    (tx) => tx.reconciliationStatus === 'reconciled',
  ).length;
  const unmatched = transactions.filter(
    (tx) => tx.reconciliationStatus === 'unmatched',
  ).length;
  const pending = transactions.filter(
    (tx) => tx.reconciliationStatus === 'pending',
  ).length;
  const total = transactions.length;
  const matchRate = total > 0 ? Math.round((matched / total) * 100) : 0;

  // Unmatched = no category OR status explicitly unmatched
  const needsAttention = transactions.filter(
    (tx) => tx.reconciliationStatus === 'unmatched' || !tx.categoryId,
  );

  // ── Batches ────────────────────────────────────────────────────────────────
  const loadBatches = async () => {
    try {
      setLoadingBatches(true);
      const res = await new FinanceService().getReconciliationBatches();
      // Safely unwrap — endpoint may return array directly or paginated
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
      setBatches(data);
    } catch {
      enqueueSnackbar('Failed to load batches', { variant: 'error' });
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleReconcile = async (batchId: number) => {
    try {
      setReconcilingId(batchId);
      await new FinanceService().reconcileBatch(String(batchId));
      enqueueSnackbar('Batch reconciled successfully!');
      loadBatches();
      onRefresh();
    } catch {
      enqueueSnackbar('Failed to reconcile batch', { variant: 'error' });
    } finally {
      setReconcilingId(null);
    }
  };

  // Mark a single transaction as reviewed (manually reconciled)
  const handleMarkReviewed = async (tx: IBankTransaction) => {
    try {
      setMarkingReviewed(tx.id as number);
      await new FinanceService().markTransactionReviewed(String(tx.id));
      enqueueSnackbar('Transaction marked as reviewed');
      onRefresh();
    } catch {
      enqueueSnackbar('Failed to mark as reviewed', { variant: 'error' });
    } finally {
      setMarkingReviewed(null);
    }
  };

  const handleAssignSave = async (
    txId: string,
    categoryId: string,
    reason: string,
  ) => {
    try {
      await new FinanceService().assignCategory(txId, categoryId, reason);
      enqueueSnackbar(
        'Category assigned — transaction will reconcile on next batch run',
      );
      setAssignOpen(false);
      setSelectedTx(null);
      onRefresh();
    } catch {
      enqueueSnackbar('Failed to assign category', { variant: 'error' });
    }
  };

  useEffect(() => {
    loadBatches();
  }, []);

  const getCategoryName = (categoryId?: number) => {
    if (!categoryId) return null;
    return (
      categories.find((c) => c.id === categoryId)?.name ?? `#${categoryId}`
    );
  };

  return (
    <Stack spacing={2.5}>
      {/* ── KPI cards ── */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Matched / Reconciled"
            value={matched}
            color="success"
            icon="eva:checkmark-circle-2-fill"
            sub="Categorised & reconciled"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Unmatched"
            value={unmatched}
            color="error"
            icon="eva:alert-circle-fill"
            sub="Needs category or review"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Pending"
            value={pending}
            color="warning"
            icon="eva:clock-fill"
            sub="Not yet reconciled"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Match rate"
            value={`${matchRate}%`}
            color="info"
            icon="eva:pie-chart-2-fill"
            sub={`${total} total transactions`}
          />
        </Grid>
      </Grid>

      {/* ── Progress bar ── */}
      <Card sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle2">Reconciliation progress</Typography>
          <Typography
            variant="body2"
            fontFamily="monospace"
            color="text.secondary"
          >
            {matched} / {total} reconciled
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={matchRate}
          color={
            matchRate >= 90 ? 'success' : matchRate >= 60 ? 'warning' : 'error'
          }
          sx={{ height: 8, borderRadius: 1 }}
        />
        <Stack direction="row" spacing={3} mt={1.5}>
          {[
            { label: 'Reconciled', color: 'success.main', count: matched },
            { label: 'Unmatched', color: 'error.main', count: unmatched },
            { label: 'Pending', color: 'warning.main', count: pending },
          ].map((item) => (
            <Stack
              key={item.label}
              direction="row"
              alignItems="center"
              spacing={0.75}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: item.color,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {item.label}: <strong>{item.count}</strong>
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Card>

      {/* ── How reconciliation works ── */}
      <Alert severity="info" sx={{ borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          How to reconcile
        </Typography>
        <Typography variant="caption" display="block">
          1. Make sure all transactions have a category assigned (Bank
          Transactions tab).
        </Typography>
        <Typography variant="caption" display="block">
          2. Click <strong>Create Batch</strong> — select a date range (e.g. Aug
          2022).
        </Typography>
        <Typography variant="caption" display="block">
          3. The system marks all categorised transactions in that period as{' '}
          <strong>Reconciled</strong>. Uncategorised ones become{' '}
          <strong>Unmatched</strong>.
        </Typography>
      </Alert>

      {/* ── Transactions needing attention ── */}
      {needsAttention.length > 0 && (
        <Stack spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subtitle2">
              Needs attention ({pluralize(needsAttention.length, 'transaction')}
              )
            </Typography>
          </Stack>
          <Card>
            <Scrollbar>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Narrative</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {needsAttention.map((tx) => {
                    const isCredit = tx.direction === 'credit';
                    const catName = getCategoryName(tx.categoryId);
                    return (
                      <TableRow
                        key={tx.id}
                        hover
                        sx={{
                          bgcolor: !tx.categoryId
                            ? 'warning.lighter'
                            : 'inherit',
                        }}
                      >
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {fDate(tx.transactionDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 200 }}
                          >
                            {tx.narrative}
                          </Typography>
                          {tx.counterparty && (
                            <Typography variant="caption" color="text.disabled">
                              {tx.counterparty}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.75,
                              bgcolor: 'grey.100',
                              fontFamily: 'monospace',
                              fontWeight: 600,
                            }}
                          >
                            {tx.transactionType ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            fontWeight={500}
                            color={isCredit ? 'success.dark' : 'error.dark'}
                          >
                            {isCredit ? '+' : '−'}
                            {fCurrency(Math.abs(tx.amount))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {catName ? (
                            <Chip
                              label={catName}
                              size="small"
                              variant="soft"
                              color="default"
                            />
                          ) : (
                            <Chip
                              label="Unclassified"
                              size="small"
                              color="warning"
                              variant="soft"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={tx.reconciliationStatus ?? 'pending'}
                            size="small"
                            color={
                              tx.reconciliationStatus === 'reconciled'
                                ? 'success'
                                : tx.reconciliationStatus === 'unmatched'
                                  ? 'error'
                                  : 'warning'
                            }
                            variant="soft"
                            sx={{ textTransform: 'capitalize', fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                          >
                            {/* Assign category */}
                            <Tooltip title="Assign category">
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: 11,
                                  py: 0.25,
                                  minWidth: 0,
                                  px: 1,
                                }}
                                onClick={() => {
                                  setSelectedTx(tx);
                                  setAssignOpen(true);
                                }}
                              >
                                Assign
                              </Button>
                            </Tooltip>
                            {/* Mark as reviewed */}
                            <Tooltip title="Mark as reviewed (manually reconcile)">
                              <IconButton
                                size="small"
                                disabled={markingReviewed === tx.id}
                                onClick={() => handleMarkReviewed(tx)}
                              >
                                {markingReviewed === tx.id ? (
                                  <CircularProgress size={12} />
                                ) : (
                                  <Iconify
                                    icon="eva:checkmark-circle-2-fill"
                                    width={14}
                                  />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Scrollbar>
          </Card>
        </Stack>
      )}

      {/* ── Batch history ── */}
      <Stack spacing={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle2">Reconciliation batches</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<Iconify icon="eva:refresh-fill" />}
              onClick={loadBatches}
              disabled={loadingBatches}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => setCreateBatchOpen(true)}
            >
              Create Batch
            </Button>
          </Stack>
        </Stack>

        {loadingBatches ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : batches.length === 0 ? (
          <Card sx={{ py: 6, textAlign: 'center' }}>
            <Iconify
              icon="eva:inbox-fill"
              width={40}
              sx={{
                color: 'text.disabled',
                display: 'block',
                mx: 'auto',
                mb: 1,
              }}
            />
            <Typography variant="body2" color="text.disabled">
              No reconciliation batches yet
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Click "Create Batch" to start your first reconciliation
            </Typography>
          </Card>
        ) : (
          <Card>
            <Scrollbar>
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell align="center">Total</TableCell>
                    <TableCell align="center">Matched</TableCell>
                    <TableCell align="center">Unmatched</TableCell>
                    <TableCell align="center">Match rate</TableCell>
                    <TableCell align="right">Variance</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batches.map((batch) => {
                    const rate = Math.round((batch.matchRate ?? 0) * 100);
                    return (
                      <TableRow key={batch.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {fDate(batch.periodStart)} —{' '}
                            {fDate(batch.periodEnd)}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            Created {fDate(batch.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontFamily="monospace">
                            {batch.totalTransactions}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            color="success.dark"
                          >
                            {batch.matchedCount}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            color={
                              batch.unmatchedCount > 0
                                ? 'error.dark'
                                : 'text.secondary'
                            }
                          >
                            {batch.unmatchedCount}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            justifyContent="center"
                          >
                            <LinearProgress
                              variant="determinate"
                              value={rate}
                              color={
                                rate >= 90
                                  ? 'success'
                                  : rate >= 70
                                    ? 'warning'
                                    : 'error'
                              }
                              sx={{ width: 60, height: 6, borderRadius: 1 }}
                            />
                            <Typography
                              variant="caption"
                              fontFamily="monospace"
                            >
                              {rate}%
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            color={
                              Math.abs(batch.variance ?? 0) > 0
                                ? 'warning.dark'
                                : 'text.secondary'
                            }
                          >
                            {batch.variance ? fCurrency(batch.variance) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={batch.status ?? 'pending'}
                            size="small"
                            color={
                              batch.status === 'completed'
                                ? 'success'
                                : batch.status === 'failed'
                                  ? 'error'
                                  : 'warning'
                            }
                            variant="soft"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant={
                              batch.status === 'completed'
                                ? 'outlined'
                                : 'contained'
                            }
                            disabled={reconcilingId === batch.id}
                            startIcon={
                              reconcilingId === batch.id ? (
                                <CircularProgress size={12} color="inherit" />
                              ) : undefined
                            }
                            onClick={() => handleReconcile(batch.id as number)}
                            sx={{ fontSize: 11 }}
                          >
                            {reconcilingId === batch.id
                              ? 'Running…'
                              : batch.status === 'completed'
                                ? 'Re-run'
                                : 'Reconcile'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Scrollbar>
          </Card>
        )}
      </Stack>

      {/* ── Dialogs ── */}
      <CreateBatchDialog
        open={createBatchOpen}
        onClose={() => setCreateBatchOpen(false)}
        onCreated={loadBatches}
      />

      <AssignCategoryDialog
        open={assignOpen}
        transaction={selectedTx}
        categories={categories}
        onClose={() => {
          setAssignOpen(false);
          setSelectedTx(null);
        }}
        onSave={handleAssignSave}
      />
    </Stack>
  );
}
